<template>
  <div class="cc-root">
    <!-- Platzhalter im Seitenfluss, damit der Banner keinen Inhalt dauerhaft verdeckt (z. B. Datenschutzerklärung) -->
    <div v-if="open" :style="{ height: `${spacerHeight}px` }" aria-hidden="true" class="cc-spacer"/>
    <transition name="cc-slide">
      <section v-if="open" ref="panel" :class="{ 'cc--settings': view === 'settings' }" aria-describedby="cc-desc"
               aria-labelledby="cc-title" aria-modal="false" class="cc" role="dialog" tabindex="-1" @keydown.esc="closeIfDecided">
        <header class="cc__head">
          <span class="cc__avatar tone-info"><v-icon class="cc__avatar-icon" small>mdi-cookie-outline</v-icon></span>
          <div class="cc__head-text">
            <h2 id="cc-title" class="cc__title">{{ view === 'settings' ? 'Cookie-Einstellungen' : 'Deine Privatsphäre' }}</h2>
            <div class="cc__sub">{{ subtitle }}</div>
          </div>
          <button v-if="view === 'settings' && !reopened" aria-label="Zurück zur Übersicht" class="cc__icon-btn" type="button"
                  @click="view = 'intro'">
            <v-icon small>mdi-arrow-left</v-icon>
          </button>
          <button v-if="decided" aria-label="Schließen, Auswahl unverändert lassen" class="cc__icon-btn" type="button" @click="closeIfDecided">
            <v-icon small>mdi-close</v-icon>
          </button>
        </header>

        <div class="cc__body">
          <template v-if="view === 'intro'">
            <p id="cc-desc" class="cc__text">
              Wir nutzen notwendige Technologien, damit Vanilla funktioniert. Wenn du zustimmst, setzen wir zusätzlich
              <strong>Google Analytics</strong> (Statistik) sowie <strong>Meta Pixel</strong> und <strong>TikTok Pixel</strong>
              (Marketing) ein, um unsere Anmelde- und Bewerbungsstrecken zu verbessern und den Erfolg unserer Werbung zu messen.
              Dabei werden Cookies gesetzt und Nutzungsdaten (z. B. IP-Adresse, aufgerufene Seiten) an diese Anbieter übermittelt –
              auch in die USA, wo ein Zugriff durch Behörden nicht ausgeschlossen werden kann.
            </p>
            <p class="cc__text cc__text--muted">
              Deine Einwilligung ist freiwillig. Du kannst sie jederzeit über „Cookie-Einstellungen“ im Footer
              oder in der Datenschutzerklärung ändern oder widerrufen.
            </p>
            <div class="cc__tags">
              <span v-for="cat in categories" :key="cat.key" :class="cat.tone" class="tag">
                <v-icon class="tag__icon" x-small>{{ cat.icon }}</v-icon>{{ cat.title }}
              </span>
            </div>
          </template>

          <template v-else>
            <p id="cc-desc" class="cc__text cc__text--muted">
              Wähle aus, welchen Kategorien du zustimmst. Ohne Zustimmung bleiben Statistik und Marketing aus.
            </p>
            <div class="cc__cats">
              <article v-for="cat in categories" :key="cat.key" class="cc-cat">
                <div class="cc-cat__head">
                  <span :class="cat.tone" class="cc-cat__icon"><v-icon class="cc__avatar-icon" small>{{ cat.icon }}</v-icon></span>
                  <div class="cc-cat__title">{{ cat.title }}</div>
                  <span v-if="cat.required" class="tag tag--sm tone-good">Immer aktiv</span>
                  <v-switch v-else v-model="choices[cat.key]" :aria-label="`${cat.title} erlauben`" class="cc-cat__switch mt-0 pt-0"
                            color="info" dense hide-details inset/>
                </div>
                <p class="cc-cat__desc">{{ cat.description }}</p>
                <button :aria-expanded="String(!!expanded[cat.key])" class="add-link" type="button" @click="toggle(cat.key)">
                  <v-icon x-small>{{ expanded[cat.key] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  {{ expanded[cat.key] ? 'Details ausblenden' : `Anbieter & Details (${cat.providers.map((p) => p.name).join(', ')})` }}
                </button>
                <v-expand-transition>
                  <div v-if="expanded[cat.key]" class="cc-cat__providers">
                    <dl v-for="provider in cat.providers" :key="provider.name" class="cc-prov">
                      <div class="cc-prov__name">{{ provider.name }} <span class="cc-prov__company">· {{ provider.company }}</span></div>
                      <div class="cc-prov__row"><dt>Zweck</dt>
                        <dd>{{ provider.purpose }}</dd>
                      </div>
                      <div class="cc-prov__row"><dt>Einsatz</dt>
                        <dd>{{ provider.scope }}</dd>
                      </div>
                      <div class="cc-prov__row"><dt>Speicherung</dt>
                        <dd>{{ provider.storage }}</dd>
                      </div>
                      <div v-if="provider.thirdCountry" class="cc-prov__row"><dt>Drittland</dt>
                        <dd>{{ provider.thirdCountry }}</dd>
                      </div>
                    </dl>
                  </div>
                </v-expand-transition>
              </article>
            </div>
            <p v-if="decidedAt" class="cc__text cc__text--muted cc__text--small">Deine aktuelle Auswahl stammt vom {{ decidedAt }}.</p>
          </template>
        </div>

        <footer class="cc__foot">
          <div class="cc__actions">
            <!-- Ablehnen und Akzeptieren sind bewusst gleich gestaltet (keine Hervorhebung einer Option) -->
            <button :class="choiceClass" class="cta cta--lg cc__btn" type="button" @click="rejectAll">Alle ablehnen</button>
            <button :class="choiceClass" class="cta cta--lg cc__btn" type="button" @click="acceptAll">Alle akzeptieren</button>
            <button v-if="view === 'intro'" class="cta cta--quiet cta--lg cc__btn cc__btn--wide" type="button" @click="openSettings">
              <v-icon class="cta__icon" small>mdi-tune-variant</v-icon>
              Einstellungen
            </button>
            <button v-else class="cta cta--primary cta--lg cc__btn cc__btn--wide" type="button" @click="saveSelection">
              <v-icon class="cta__icon" small>mdi-check</v-icon>
              Auswahl speichern
            </button>
          </div>
          <nav aria-label="Rechtliches" class="cc__links">
            <a :href="legalHref('/compliance')" rel="noopener" target="_blank">Datenschutzerklärung</a>
            <span aria-hidden="true">·</span>
            <a :href="legalHref('/imprint')" rel="noopener" target="_blank">Impressum</a>
          </nav>
        </footer>
      </section>
    </transition>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {CONSENT_CATEGORIES} from '@/components/app/cookieConsent.data';

type View = 'intro' | 'settings';

export default Vue.extend({
  data: () => ({
    view: 'intro' as View,
    choices: {statistics: false, marketing: false} as Record<string, boolean>,
    expanded: {} as Record<string, boolean>,
    decided: false,
    reopened: false,
    decidedAt: '',
    spacerHeight: 0,
    resizeObserver: null as ResizeObserver | null,
  }),
  computed: {
    open(): boolean {
      return !!this.$store.state.showCookieDialog;
    },
    categories() {
      return CONSENT_CATEGORIES;
    },
    subtitle(): string {
      return this.view === 'settings' ? 'Notwendig · Statistik · Marketing' : 'Du entscheidest, was wir nutzen dürfen.';
    },
    /** Erste Ebene: beide Optionen primär; in den Einstellungen beide sekundär (Speichern ist primär) */
    choiceClass(): string {
      return this.view === 'intro' ? 'cta--primary' : '';
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(val: boolean) {
        if (val) {
          this.prepare();
          this.$nextTick(() => this.observePanel());
        } else {
          this.disconnect();
        }
      },
    },
  },
  beforeDestroy() {
    this.disconnect();
  },
  methods: {
    /** Beim (erneuten) Öffnen den aktuellen Stand anzeigen – nie etwas vorausgewählt, was nicht gewählt wurde */
    prepare() {
      const manager = this.$$cookieManager;
      this.decided = manager.hasDecided();
      const settings = manager.getCookieSettings();
      this.choices = {
        statistics: this.decided && manager.hasConsent('statistics'),
        marketing: this.decided && manager.hasConsent('marketing'),
      };
      this.decidedAt = this.decided && settings.timestamp
          ? new Date(settings.timestamp).toLocaleString('de-DE', {dateStyle: 'medium', timeStyle: 'short'})
          : '';
      this.expanded = {};
      // Über den Footer erneut geöffnet → direkt die granulare Auswahl zeigen
      this.reopened = this.decided;
      this.view = this.decided ? 'settings' : 'intro';
    },
    openSettings() {
      this.view = 'settings';
      this.$nextTick(() => this.focusPanel());
    },
    toggle(key: string) {
      this.$set(this.expanded, key, !this.expanded[key]);
    },
    acceptAll() {
      this.$$cookieManager.acceptAll();
    },
    rejectAll() {
      this.$$cookieManager.rejectAll();
    },
    saveSelection() {
      this.$$cookieManager.saveConsent({
        statistics: !!this.choices.statistics,
        marketing: !!this.choices.marketing,
      });
    },
    closeIfDecided() {
      if (this.decided) this.$store.commit('showCookieDialog', false);
    },
    legalHref(path: string): string {
      return this.$router.resolve(path).href;
    },
    focusPanel() {
      const panel = this.$refs.panel as HTMLElement | undefined;
      panel?.focus({preventScroll: true});
    },
    observePanel() {
      const panel = this.$refs.panel as HTMLElement | undefined;
      if (!panel) return;
      this.focusPanel();
      this.spacerHeight = panel.offsetHeight;
      if (typeof ResizeObserver === 'undefined') return;
      this.disconnect();
      this.resizeObserver = new ResizeObserver(() => {
        this.spacerHeight = panel.offsetHeight;
      });
      this.resizeObserver.observe(panel);
    },
    disconnect() {
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
    },
  },
});
</script>

<style scoped>
.cc {
  position: fixed;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  z-index: 1001;
  width: calc(100vw - 32px);
  max-width: 560px;
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  background: #FFFFFF;
  color: rgba(15, 23, 42, .9);
  box-shadow: 0 1px 2px rgba(15, 23, 42, .08), 0 16px 40px rgba(15, 23, 42, .18);
  outline: none;
  font-size: 14px;
}

.cc--settings {
  max-width: 640px;
}

.cc__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px 6px;
}

.cc__head-text {
  flex: 1 1 auto;
  min-width: 0;
}

.cc__avatar, .cc-cat__icon {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: grid;
  place-items: center;
}

.cc__avatar-icon {
  color: inherit !important;
}

.cc__title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
}

.cc__sub {
  font-size: 12px;
  color: rgba(15, 23, 42, .6);
}

.cc__icon-btn {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: grid;
  place-items: center;
  cursor: pointer;
}

.cc__icon-btn:hover {
  background: rgba(15, 23, 42, .06);
}

.cc__icon-btn:focus-visible, .cc a:focus-visible {
  outline: 2px solid #1976D2;
  outline-offset: 2px;
}

.cc__body {
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 6px 20px 4px;
}

.cc__text {
  margin: 0 0 10px;
  line-height: 1.5;
}

.cc__text--muted {
  color: rgba(15, 23, 42, .65);
}

.cc__text--small {
  font-size: 12px;
  margin-top: 10px;
}

.cc__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}

.cc__cats {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cc-cat {
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(15, 23, 42, .04);
}

.cc-cat__head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cc-cat__title {
  flex: 1 1 auto;
  font-weight: 600;
}

.cc-cat__switch {
  flex: 0 0 auto;
}

.cc-cat__desc {
  margin: 8px 0 6px;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(15, 23, 42, .7);
}

.cc-cat .add-link {
  text-align: left;
  font-size: 12px;
}

.cc-cat__providers {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.cc-prov {
  margin: 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: #FFFFFF;
  font-size: 12px;
  line-height: 1.45;
}

.cc-prov__name {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
}

.cc-prov__company {
  font-weight: 400;
  color: rgba(15, 23, 42, .6);
}

.cc-prov__row {
  display: grid;
  grid-template-columns: 84px 1fr;
  gap: 8px;
  margin-top: 2px;
}

.cc-prov dt {
  color: rgba(15, 23, 42, .55);
}

.cc-prov dd {
  margin: 0;
}

.cc__foot {
  padding: 12px 20px 14px;
}

.cc__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.cc__btn {
  width: 100%;
}

.cc__btn--wide {
  grid-column: 1 / -1;
}

.cc__links {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 12px;
  color: rgba(15, 23, 42, .5);
}

.cc__links a {
  color: rgba(15, 23, 42, .65);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.cc__links a:hover {
  color: #1565C0;
}

.cc-slide-enter-active, .cc-slide-leave-active {
  transition: opacity .2s ease, transform .2s ease;
}

.cc-slide-enter, .cc-slide-leave-to {
  opacity: 0;
  transform: translate(-50%, 16px);
}

/* Telefon: volle Breite, am unteren Rand angedockt */
@media (max-width: 599px) {
  .cc {
    left: 0;
    bottom: 0;
    transform: none;
    width: 100%;
    max-width: none;
    max-height: 88vh;
    border-radius: 16px 16px 0 0;
  }

  .cc--settings {
    max-width: none;
  }

  .cc__head {
    padding: 16px 16px 4px;
  }

  .cc__body {
    padding: 6px 16px 4px;
  }

  .cc__foot {
    padding: 10px 16px calc(12px + env(safe-area-inset-bottom));
  }

  .cc-slide-enter, .cc-slide-leave-to {
    transform: translateY(16px);
  }

  .cc-prov__row {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .cc-prov dt {
    font-size: 11px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cc-slide-enter-active, .cc-slide-leave-active {
    transition: none;
  }
}

/* Dark mode */
.theme--dark .cc {
  background: #1E1E1E;
  color: rgba(255, 255, 255, .9);
  box-shadow: 0 1px 2px rgba(0, 0, 0, .4), 0 16px 40px rgba(0, 0, 0, .55);
}

.theme--dark .cc__sub, .theme--dark .cc__text--muted, .theme--dark .cc-prov__company, .theme--dark .cc-prov dt {
  color: rgba(255, 255, 255, .6);
}

.theme--dark .cc-cat__desc {
  color: rgba(255, 255, 255, .72);
}

.theme--dark .cc-cat {
  background: rgba(255, 255, 255, .06);
}

.theme--dark .cc-prov {
  background: rgba(255, 255, 255, .05);
}

.theme--dark .cc__icon-btn:hover {
  background: rgba(255, 255, 255, .08);
}

.theme--dark .cc__links, .theme--dark .cc__links a {
  color: rgba(255, 255, 255, .6);
}

.theme--dark .cc__links a:hover {
  color: #90CAF9;
}

.theme--dark .cc .cta:not(.cta--primary) {
  background: rgba(144, 202, 249, .14);
  color: #90CAF9;
}

.theme--dark .cc .cta.cta--quiet {
  background: rgba(255, 255, 255, .08);
  color: rgba(255, 255, 255, .85);
}

.theme--dark .cc .add-link {
  color: rgba(255, 255, 255, .55);
}

.theme--dark .cc .add-link:hover {
  color: #90CAF9;
}
</style>
<style src="@/components/common/modern/modern.css"></style>
